import React, { memo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import alignmentSessionStyles from "./styles";
import { getHeatmapCriticality } from "./utils";

const useStyles = makeStyles(alignmentSessionStyles);

// Month abbreviations for header (matching bar chart labels)
const MONTH_ABBREV = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/**
 * HeatmapTable component
 * Displays a margin heatmap table showing values by month and year
 * Colors indicate relative performance (red = worst, green = best)
 */
function HeatmapTable({ heatmapData }) {
  const classes = useStyles();

  // Collect all non-null values for criticality calculation
  const allValues = heatmapData.years
    .flatMap((year) => heatmapData.values[year])
    .filter((v) => v !== null);

  // Check if a year has any data
  const yearHasData = (year) =>
    heatmapData.values[year]?.some((v) => v !== null);

  return (
    <table className={classes.heatmapTable}>
      <thead>
        <tr>
          <th className={classes.heatmapYearHeader}></th>
          {heatmapData.months.map((m, idx) => (
            <th key={m} className={classes.heatmapMonthHeader}>
              {MONTH_ABBREV[idx]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {heatmapData.years.map((year) => {
          const hasData = yearHasData(year);
          return (
            <tr key={year}>
              <td className={classes.heatmapYearCell}>
                {String(year).slice(-2)}
              </td>
              {heatmapData.values[year].map((val, idx) => {
                const isNoData = val === null;
                const hmClass =
                  !isNoData && hasData
                    ? `hm${getHeatmapCriticality(val, allValues)}`
                    : "";
                const noDataClass = isNoData ? classes.heatmapNoData : "";
                return (
                  <td
                    key={idx}
                    className={`${classes.heatmapCell} ${hmClass} ${noDataClass}`}
                  >
                    {val !== null ? `${val.toFixed(1)}%` : "-"}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

HeatmapTable.propTypes = {
  heatmapData: PropTypes.shape({
    years: PropTypes.arrayOf(PropTypes.number).isRequired,
    months: PropTypes.arrayOf(PropTypes.number).isRequired,
    values: PropTypes.object.isRequired,
  }).isRequired,
};

export default memo(HeatmapTable);
